import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  getVersionHistory,
  getKnowledgePack,
  rollbackKnowledgePack,
  formatDateTime,
} from '../api/knowledgePacks';

/**
 * NEXUS V6.3 Phase 2B - Version History Viewer
 * Timeline view with compare and restore functionality
 */
const KnowledgePackVersionHistory = () => {
  const navigate = useNavigate();
  const { packId } = useParams();
  const { user } = useAuth();

  const [pack, setPack] = useState(null);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [compareMode, setCompareMode] = useState(false);
  const [selectedVersions, setSelectedVersions] = useState([]);

  const isAdmin = user?.role === 'admin';
  const isManagement = user?.role === 'management' || user?.role === 'management_functional';
  const canRollback = isAdmin || isManagement;

  useEffect(() => {
    loadData();
  }, [packId]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [packResponse, versionResponse] = await Promise.all([
        getKnowledgePack(packId, user?.role),
        getVersionHistory(packId),
      ]);

      setPack(packResponse.pack);
      setVersions(versionResponse.versions || []);
    } catch (err) {
      console.error('Failed to load version history:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRollback = async (versionNum) => {
    if (!canRollback) {
      alert('You do not have permission to rollback versions');
      return;
    }

    const confirmRollback = window.confirm(
      `Are you sure you want to rollback to version ${versionNum}? This will create a new version with the same content.`
    );

    if (!confirmRollback) return;

    try {
      const reason = prompt('Reason for rollback (optional):');
      await rollbackKnowledgePack(
        packId,
        versionNum,
        user?.user_id,
        reason || 'Rollback via UI',
        user?.role
      );
      alert('Rollback successful!');
      loadData(); // Refresh
    } catch (err) {
      console.error('Rollback failed:', err);
      alert(`Rollback failed: ${err.message}`);
    }
  };

  const handleCompareToggle = (versionId) => {
    if (selectedVersions.includes(versionId)) {
      setSelectedVersions(selectedVersions.filter(v => v !== versionId));
    } else if (selectedVersions.length < 2) {
      setSelectedVersions([...selectedVersions, versionId]);
    } else {
      alert('You can only compare 2 versions at a time');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white" style={{ height: '100dvh' }}>
      {/* Header */}
      <div className="bg-gradient-to-r from-[#0e67a2] to-[#0a5a8a] text-white px-4 py-3 shadow-md">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center text-lg">
              📜
            </div>
            <div>
              <h1 className="text-lg font-black leading-tight">Version History</h1>
              <p className="text-xs font-medium opacity-90 truncate">
                {pack?.name || 'Loading...'}
              </p>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1">
            {(isAdmin || isManagement) && (
              <span className="text-[10px] font-medium px-2 py-0.5 bg-white/20 rounded">
                🟢 ISO Compliance Active
              </span>
            )}
            <button
              onClick={() => navigate('/knowledge-packs')}
              className="text-xs font-semibold px-3 py-1 bg-white/10 hover:bg-white/20 rounded transition"
            >
              ← Back
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 pb-20 overflow-y-auto" style={{ maxHeight: 'calc(100dvh - 64px)' }}>
        {/* Controls */}
        <div className="bg-white rounded-xl shadow-sm p-3 mb-3 flex items-center justify-between">
          <div>
            <span className="text-sm font-bold text-gray-900">
              {versions.length} Version{versions.length !== 1 ? 's' : ''}
            </span>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setCompareMode(!compareMode)}
              className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition ${
                compareMode
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {compareMode ? '✓ Compare' : 'Compare'}
            </button>
            <button
              onClick={loadData}
              className="px-3 py-1.5 bg-gray-100 text-gray-700 text-xs font-semibold rounded-lg hover:bg-gray-200 transition"
            >
              🔄
            </button>
          </div>
        </div>

        {/* Loading */}
        {loading && (
          <div className="bg-white rounded-xl shadow-sm p-8 text-center">
            <div className="text-4xl mb-2">⏳</div>
            <p className="text-sm font-medium text-gray-600">Loading versions...</p>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-3">
            <p className="text-sm font-semibold text-red-600">⚠️ {error}</p>
            <button onClick={loadData} className="mt-2 text-xs font-semibold text-red-600 underline">
              Try Again
            </button>
          </div>
        )}

        {/* Timeline */}
        {!loading && !error && versions.length > 0 && (
          <div className="space-y-3">
            {versions.map((version, idx) => (
              <VersionCard
                key={version.version_id}
                version={version}
                isLatest={idx === 0}
                isActive={version.is_published}
                canRollback={canRollback}
                compareMode={compareMode}
                isSelected={selectedVersions.includes(version.version_id)}
                onRollback={() => handleRollback(version.version)}
                onCompareToggle={() => handleCompareToggle(version.version_id)}
              />
            ))}
          </div>
        )}

        {/* Compare View */}
        {compareMode && selectedVersions.length === 2 && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-gray-900">Compare Versions</h2>
                <button
                  onClick={() => setSelectedVersions([])}
                  className="text-gray-500 hover:text-gray-700 text-xl font-bold"
                >
                  ✕
                </button>
              </div>
              <p className="text-xs text-gray-600 mb-4">
                Comparing Version {versions.find(v => v.version_id === selectedVersions[0])?.version} vs Version {versions.find(v => v.version_id === selectedVersions[1])?.version}
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-xs text-blue-800">
                  ℹ️ Compare feature coming soon. Version differences will be highlighted here.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Version Card Component
const VersionCard = ({
  version,
  isLatest,
  isActive,
  canRollback,
  compareMode,
  isSelected,
  onRollback,
  onCompareToggle,
}) => {
  return (
    <div
      className={`bg-white rounded-xl shadow-sm p-4 border-2 transition ${
        isActive ? 'border-green-500 bg-green-50/30' : 'border-gray-200'
      } ${isSelected ? 'ring-2 ring-blue-500' : ''}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-lg flex items-center justify-center font-black text-sm">
            v{version.version}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-bold text-gray-900">Version {version.version}</span>
              {isActive && (
                <span className="text-[10px] font-semibold px-2 py-0.5 bg-green-500 text-white rounded-full">
                  ✓ ACTIVE
                </span>
              )}
              {isLatest && !isActive && (
                <span className="text-[10px] font-semibold px-2 py-0.5 bg-blue-500 text-white rounded-full">
                  LATEST
                </span>
              )}
            </div>
            <p className="text-xs text-gray-600">
              Created {formatDateTime(version.created_at)}
            </p>
          </div>
        </div>

        {compareMode && (
          <button
            onClick={onCompareToggle}
            className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition ${
              isSelected
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {isSelected ? '✓ Selected' : 'Select'}
          </button>
        )}
      </div>

      {/* Details */}
      <div className="space-y-2 mb-3">
        {version.created_by && (
          <div className="text-xs">
            <span className="font-semibold text-gray-700">Created by:</span>{' '}
            <span className="text-gray-600">
              {typeof version.created_by === 'string' ? version.created_by : JSON.stringify(version.created_by)}
            </span>
          </div>
        )}
        {version.is_published && version.published_at && (
          <div className="text-xs">
            <span className="font-semibold text-gray-700">Published:</span>{' '}
            <span className="text-gray-600">{formatDateTime(version.published_at)}</span>
          </div>
        )}
        {version.changes && Object.keys(version.changes).length > 0 && (
          <div className="text-xs">
            <span className="font-semibold text-gray-700">Changes:</span>{' '}
            <span className="text-gray-600">
              {Object.keys(version.changes).map(key => {
                const value = version.changes[key];
                if (typeof value === 'object' && value !== null && (value.en || value.ms)) {
                  return `${key} (${value.en || value.ms || 'bilingual'})`;
                }
                return key;
              }).join(', ')}
            </span>
          </div>
        )}
      </div>

      {/* Actions */}
      {!compareMode && canRollback && !isActive && (
        <div className="pt-3 border-t border-gray-200">
          <button
            onClick={onRollback}
            className="w-full py-2 bg-blue-50 text-blue-600 text-xs font-semibold rounded-lg hover:bg-blue-100 transition"
          >
            ← Rollback to this version
          </button>
        </div>
      )}
    </div>
  );
};

export default KnowledgePackVersionHistory;